import { DeleteOutlined, RobotOutlined } from "@ant-design/icons";
import { Input, Popconfirm, Radio } from "antd";
import { noteApi } from "api/note.api";
import { AppButton } from "components/antd-ui";
import { CommentAvatar } from "components/Comment/CommentComposer";
import { observer } from "mobx-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { userStore } from "store/userStore";
import { Note, NoteRefType, NoteType } from "types/note";
import { formatDateTime } from "utils/date";
import "./NoteLog.scss";

const PAGE_SIZE = 20;

type NoteLogProps = {
  refType: NoteRefType;
  refId: number;
};

type Filter = "ALL" | NoteType;

const linkOf = (note: Note) => {
  if (note.linkType === "OPPORTUNITY" && note.linkId) return { to: `/opportunity/${note.linkId}`, label: "Xem" };
  if (note.linkType === "ORDER" && note.linkId) return { to: `/order?orderId=${note.linkId}`, label: "Xem đơn hàng" };
  return null;
};

// Ghi chú dạng log dùng chung cho nhiều menu (gắn theo refType + refId, giống CommentThread):
// ghi chú tay do nhân viên nhập, ghi chú tự động do hệ thống ghi (chỉ đọc)
export const NoteLog = observer(({ refType, refId }: NoteLogProps) => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<Filter>("ALL");
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const currentStaffId = userStore.info.id;
  const isAdmin = !!userStore.info.role?.isAdmin;

  const load = useCallback(
    async (nextPage: number, append: boolean) => {
      setLoading(true);
      if (!append) setNotes([]);
      try {
        const res = await noteApi.findAll({
          page: nextPage,
          limit: PAGE_SIZE,
          refType,
          refId,
          type: filter === "ALL" ? undefined : filter,
        });
        const next: Note[] = res.data.notes || [];
        setNotes((prev) => (append ? [...prev, ...next] : next));
        setTotal(res.data.total || 0);
        setPage(nextPage);
      } finally {
        setLoading(false);
      }
    },
    [filter, refId, refType],
  );

  useEffect(() => {
    load(1, false);
  }, [load]);

  const handleCreate = useCallback(async () => {
    const text = content.trim();
    if (!text) return;
    setSaving(true);
    try {
      const res = await noteApi.create({ note: { content: text, refType, refId } });
      setContent("");
      // Đang lọc "Tự động" thì ghi chú tay mới không thuộc danh sách này
      if (filter !== NoteType.System) {
        setNotes((prev) => [res.data, ...prev]);
        setTotal((prev) => prev + 1);
      }
    } finally {
      setSaving(false);
    }
  }, [content, filter, refId, refType]);

  const handleDelete = useCallback(async (noteId: number) => {
    await noteApi.delete(noteId);
    setNotes((prev) => prev.filter((note) => note.id !== noteId));
    setTotal((prev) => Math.max(0, prev - 1));
  }, []);

  return (
    <div className="noteLog">
      <div className="noteLog__composer">
        <Input.TextArea
          value={content}
          rows={3}
          maxLength={2000}
          placeholder="Thêm ghi chú"
          onChange={(event) => setContent(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && event.shiftKey) {
              event.preventDefault();
              handleCreate();
            }
          }}
        />
        <div className="noteLog__composerActions">
          <AppButton tone="primary" loading={saving} disabled={!content.trim()} onClick={handleCreate}>
            Thêm ghi chú
          </AppButton>
          <span className="noteLog__shortcut">Shift + Enter</span>
        </div>
      </div>

      <Radio.Group
        size="small"
        optionType="button"
        value={filter}
        onChange={(event) => setFilter(event.target.value)}
        options={[
          { label: "Tất cả", value: "ALL" },
          { label: "Ghi chú tay", value: NoteType.Manual },
          { label: "Tự động", value: NoteType.System },
        ]}
      />

      <div className="noteLog__list">
        {!loading && notes.length === 0 && <div className="noteLog__empty">Chưa có ghi chú</div>}
        {notes.map((note) => {
          const isSystem = note.type === NoteType.System;
          const link = linkOf(note);
          const canDelete = !isSystem && (isAdmin || note.staff?.id === currentStaffId);
          return (
            <div key={note.id} className={`noteLog__item ${isSystem ? "isSystem" : ""}`}>
              <CommentAvatar avatar={note.staff?.avatar} name={note.staff?.name || ""} />
              <div className="noteLog__body">
                <div className="noteLog__meta">
                  <span className="noteLog__name">{note.staff?.name || "--"}</span>
                  <span>{formatDateTime(note.createdAt)}</span>
                  {isSystem && (
                    <span className="noteLog__tag">
                      <RobotOutlined /> Tự động
                    </span>
                  )}
                  {canDelete && (
                    <Popconfirm title="Xóa ghi chú này?" okText="Xóa" cancelText="Hủy" onConfirm={() => handleDelete(note.id)}>
                      <AppButton aria-label="Xóa ghi chú" icon={<DeleteOutlined />} />
                    </Popconfirm>
                  )}
                </div>
                <div className="noteLog__content">
                  {note.content}
                  {link && (
                    <Link className="noteLog__link" to={link.to}>
                      {link.label}
                    </Link>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {notes.length < total && (
          <AppButton loading={loading} onClick={() => load(page + 1, true)}>
            Xem thêm
          </AppButton>
        )}
      </div>
    </div>
  );
});
